import { useCallback, useEffect, useState } from 'react';

import type { StartPath } from '../utils/onboardingDerivation';

export const useOnboardingStartPath = ({
  organizationId,
  activeProjectId,
  hasProject,
}: {
  organizationId?: string;
  activeProjectId?: string;
  hasProject: boolean;
}) => {
  const [startPath, setStartPath] = useState<StartPath>('sample');
  const [selectedStartOption, setSelectedStartOption] = useState<StartPath | ''>('');
  const [startChoiceCommitted, setStartChoiceCommitted] = useState(false);
  const [startChoiceError, setStartChoiceError] = useState<string | null>(null);
  const [showStartOptionPicker, setShowStartOptionPicker] = useState(false);

  const pendingStartPathStorageKey = organizationId
    ? `onboarding_start_path_${organizationId}`
    : null;
  const projectStartPathStorageKey =
    organizationId && activeProjectId
      ? `onboarding_start_path_${organizationId}_${activeProjectId}`
      : null;

  const syncStartPathFromStorage = useCallback(({
    functionalLibraryCount,
    sampleLibraryLoaded,
  }: {
    functionalLibraryCount: number;
    sampleLibraryLoaded: boolean;
  }) => {
    if (functionalLibraryCount > 0) {
      const derivedPath: StartPath = sampleLibraryLoaded ? 'sample' : 'real';
      setStartPath(derivedPath);
      setSelectedStartOption(derivedPath);
      setStartChoiceCommitted(true);
      if (pendingStartPathStorageKey) {
        localStorage.removeItem(pendingStartPathStorageKey);
      }
      if (projectStartPathStorageKey) {
        localStorage.removeItem(projectStartPathStorageKey);
      }
      setShowStartOptionPicker(false);
      return;
    }

    if (showStartOptionPicker) {
      return;
    }

    if (projectStartPathStorageKey) {
      const storedProjectPath = localStorage.getItem(projectStartPathStorageKey);
      if (storedProjectPath === 'sample' || storedProjectPath === 'real') {
        setStartPath(storedProjectPath);
        setSelectedStartOption(storedProjectPath);
        setStartChoiceCommitted(true);
        return;
      }
    }

    if (pendingStartPathStorageKey) {
      const storedPendingPath = localStorage.getItem(pendingStartPathStorageKey);
      if (storedPendingPath === 'sample' || storedPendingPath === 'real') {
        setStartPath(storedPendingPath);
        setSelectedStartOption(storedPendingPath);
        setStartChoiceCommitted(true);
      }
    }
  }, [pendingStartPathStorageKey, projectStartPathStorageKey, showStartOptionPicker]);

  useEffect(() => {
    syncStartPathFromStorage({
      functionalLibraryCount: 0,
      sampleLibraryLoaded: false,
    });
  }, [syncStartPathFromStorage]);

  const selectStartPath = (nextPath: StartPath) => {
    setSelectedStartOption(nextPath);
    setStartChoiceError(null);
  };

  const commitStartPath = () => {
    if (!selectedStartOption) {
      setStartChoiceError('Choose how you want to start before continuing.');
      return;
    }

    const nextPath = selectedStartOption;
    setStartPath(nextPath);
    setStartChoiceCommitted(true);
    if (pendingStartPathStorageKey) {
      localStorage.setItem(pendingStartPathStorageKey, nextPath);
    }
    if (projectStartPathStorageKey) {
      localStorage.setItem(projectStartPathStorageKey, nextPath);
    }
    setShowStartOptionPicker(false);
  };

  const clearStoredStartPath = () => {
    if (pendingStartPathStorageKey) {
      localStorage.removeItem(pendingStartPathStorageKey);
    }
    if (projectStartPathStorageKey) {
      localStorage.removeItem(projectStartPathStorageKey);
    }
  };

  return {
    startPath,
    setStartPath,
    selectedStartOption,
    setSelectedStartOption,
    startChoiceCommitted,
    setStartChoiceCommitted,
    startChoiceError,
    setStartChoiceError,
    showStartOptionPicker,
    setShowStartOptionPicker,
    pendingStartPathStorageKey,
    projectStartPathStorageKey,
    syncStartPathFromStorage,
    selectStartPath,
    commitStartPath,
    clearStoredStartPath,
  };
};
